import { useState } from "react";
import { X, Scale, Info } from "lucide-react";

/**
 * The Institute's guideline for the award of CPD points.
 *
 * The committee's own table, reproduced as it stands. A chapter chairman asking
 * for points has, until now, had nothing on the screen telling him what a
 * chapter seminar is actually worth or how much notice he was supposed to give,
 * and the secretariat has had nothing to point at when it cuts a figure. Both
 * sides now read the same table from the same place.
 *
 * It lives in the portal as data rather than in the database on purpose: it is
 * the committee's published guideline, amended by the committee and not by a
 * screen, and nothing in the portal computes from it — it is read by people
 * deciding a number, not by code. When the committee revises it, this array is
 * the one thing that changes.
 *
 * Reproduced as published, points and notice periods untouched. Two plain
 * spellings in the source are corrected here — "otside" and "Oragns" — and
 * nothing else is. In particular rows 15 and 16 are left as they stand: the
 * document gives them identical wording against different points and notice,
 * which is the committee's to resolve, not this file's.
 */

export type CpdGuidelineRow = {
  sn: number;
  event: string;
  /** As the table prints it — "15", or "7.5 / day" where the document says so. */
  points: string;
  notice: string;
};

export const CPD_GUIDELINE: CpdGuidelineRow[] = [
  { sn: 1,  event: "Approved International Seminar / Conference / Workshop (held outside Nigeria)", points: "15",        notice: "2 Months" },
  { sn: 2,  event: "International Seminar / Workshop (held in Nigeria)",                            points: "10",        notice: "Six Weeks" },
  { sn: 3,  event: "Leadership Retreat in Conjunction with a Business School or other Allied Body", points: "10",        notice: "Six Weeks" },
  { sn: 4,  event: "National Seminar / Conference / Workshop",                                      points: "9",         notice: "Six Weeks" },
  { sn: 5,  event: "2 Day Seminar / Workshop / Conference",                                         points: "18",        notice: "Six Weeks" },
  { sn: 6,  event: "3 Day Seminar / Workshop / Conference",                                         points: "27",        notice: "2 Months" },
  { sn: 7,  event: "Research Conference",                                                           points: "9",         notice: "6 Months" },
  { sn: 8,  event: "Organised International Educative Tour / Visit",                                points: "7",         notice: "3 Months" },
  { sn: 9,  event: "Organised International Educative Tour / Visit (Local)",                        points: "4",         notice: "3 Months" },
  { sn: 10, event: "Chapter Seminar / Conference / Workshop",                                       points: "6",         notice: "Six Weeks" },
  { sn: 11, event: "A 2 day Seminar / Workshop / Conference by Chapter or Organs of NIQS",          points: "12",        notice: "Six Weeks" },
  { sn: 12, event: "A 3 day Seminar / Workshop / Conference by Chapter or Organs of NIQS",          points: "18",        notice: "2 Months" },
  { sn: 13, event: "Chapter-Organized Educative / Professional Tour / Visit",                       points: "3",         notice: "2 Months" },
  { sn: 14, event: "Successful Presentation of Resource Paper at Approved International Events",    points: "10",        notice: "3 Months" },
  { sn: 15, event: "Successful Presentation of Resource Paper at NIQS Chapter Event.",              points: "9",         notice: "2 Months" },
  { sn: 16, event: "Successful Presentation of Resource Paper at NIQS Chapter Event.",              points: "7",         notice: "1 Months" },
  { sn: 17, event: "Successful Presentation of Resource Paper at any other Related Professional Forum.", points: "6",    notice: "1 Months" },
  { sn: 18, event: "Attendance to and Participation in other Allied Professional Seminar / Workshop.",   points: "5",    notice: "2 Months" },
  { sn: 19, event: "Published Article in NIQS Journal",                                             points: "4",         notice: "3 Months" },
  { sn: 20, event: "Published Article in other Allied Professional Publications / New Papers",      points: "3",         notice: "3 Months" },
  { sn: 21, event: "Participation as Resource Person in NIQS Approved Tutorial Class",              points: "10",        notice: "Six Weeks" },
  { sn: 22, event: "Webinar by National",                                                           points: "7.5 / day", notice: "2 Months" },
  { sn: 23, event: "Webinar by Chapter & Organs of NIQS",                                           points: "4 / day",   notice: "1 Months" },
  { sn: 24, event: "Webinar (International)",                                                       points: "4 / day",   notice: "2 Months" },
  { sn: 25, event: "Inclusion of Technical Session in Chapter GM",                                  points: "2 / day",   notice: "Three Weeks" },
];

/** The NB under the table, in the committee's own words. */
export const CPD_GUIDELINE_NOTES: string[] = [
  "Award value are to be decided based on the quality of the resource people. A full session should have credible resource people and their sessions should be above 30 min per subtheme and at least 4 subthemes to qualify for a days' session.",
  "The committee reserves the right to reduce value of CPD where these criteria are not met.",
];

/** The rows a chapter actually raises events under — marked, not filtered. */
const isChapterRow = (r: CpdGuidelineRow) => /chapter/i.test(r.event);

// ── the table ────────────────────────────────────────────────────────────────

export function CpdGuidelineTable({ highlightChapter = true }: { highlightChapter?: boolean }) {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-bold w-10">S/N</th>
              <th className="px-3 py-2 text-left font-bold">Approved CPD events</th>
              <th className="px-3 py-2 text-left font-bold whitespace-nowrap">CPD points per day</th>
              <th className="px-3 py-2 text-left font-bold whitespace-nowrap">Period of notice</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {CPD_GUIDELINE.map((r) => {
              const mine = highlightChapter && isChapterRow(r);
              return (
                <tr key={r.sn} className={mine ? "bg-blue-50/60" : undefined}>
                  <td className="px-3 py-2 text-gray-400 align-top">{r.sn}</td>
                  <td className={`px-3 py-2 align-top ${mine ? "text-gray-900 font-semibold" : "text-gray-700"}`}>
                    {r.event}
                  </td>
                  <td className="px-3 py-2 align-top font-bold text-gray-900 whitespace-nowrap">{r.points}</td>
                  <td className="px-3 py-2 align-top text-gray-600 whitespace-nowrap">{r.notice}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {highlightChapter && (
        <p className="text-[11px] text-gray-400">
          The shaded rows are the ones a chapter raises its own events under.
        </p>
      )}

      <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-amber-700">
          <Info className="w-3.5 h-3.5" /> NB
        </div>
        {CPD_GUIDELINE_NOTES.map((n, i) => (
          <p key={i} className="text-xs text-amber-900 leading-relaxed">{n}</p>
        ))}
      </div>
    </div>
  );
}

// ── the modal, and the button that opens it ──────────────────────────────────

function CpdGuidelineModal({ onClose }: { onClose: () => void }) {
  return (
    // z-[300], above the new-request and approve modals — this opens from
    // inside both of them, beside the points field it is there to answer.
    <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full max-w-3xl max-h-[94vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Scale className="w-4 h-4 text-gray-400 shrink-0" />
              Guideline for the award of CPD points
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              What the committee allows per event, and the notice it expects before one is held.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 shrink-0">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-5 sm:p-6">
          <CpdGuidelineTable />
        </div>

        <div className="px-5 sm:px-6 py-4 border-t sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 border rounded-lg text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Opens the guideline. Self-contained, because it is wanted in four places —
 * the chapter's events header, the new-request form, the chairman's edit form
 * and the secretariat's approve modal — and none of them should have to carry
 * a piece of state for it.
 *
 * `variant` only decides how loud it is: a button where it stands alone, a link
 * where it sits under the points field it explains.
 */
export function CpdGuidelineButton({
  variant = "button",
  label = "CPD guideline",
}: {
  variant?: "button" | "link";
  label?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {variant === "link" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-800 underline underline-offset-2"
        >
          <Scale className="w-3 h-3" /> {label}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <Scale className="w-4 h-4" /> {label}
        </button>
      )}

      {open && <CpdGuidelineModal onClose={() => setOpen(false)} />}
    </>
  );
}
